import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AppConfig, ConfigParams } from '../types';

export type ConfigStatus = 'idle' | 'loading' | 'ready' | 'error';

interface ConfigState {
  config: AppConfig | null;
  status: ConfigStatus;
  setStatus: (status: ConfigStatus) => void;
  setConfig: (config: AppConfig) => void;
  clear: () => void;
}

/**
 * Holds the active funnel config for the tab/session. Persisted to
 * sessionStorage so it survives reloads and is always available within
 * the current tab once a `cfg_*` id has been resolved.
 */
export const useConfigStore = create<ConfigState>()(
  persist(
    (set) => ({
      config: null,
      status: 'idle',
      setStatus: (status) => set({ status }),
      setConfig: (config) => set({ config, status: 'ready' }),
      clear: () => set({ config: null, status: 'idle' }),
    }),
    {
      name: 'bp-config',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ config: s.config, status: s.status }),
    },
  ),
);

/** Config params for the active session (empty when no config). */
export function getConfigParams(): ConfigParams {
  return useConfigStore.getState().config?.params ?? {};
}

/**
 * Effective funnel params = config params overlaid by live URL params.
 * Live URL params win so a link can always override the stored config.
 * This is the single source every param-driven feature should read.
 */
export function getEffectiveParams(): Record<string, string> {
  const result: Record<string, string> = { ...getConfigParams() };
  if (typeof window !== 'undefined') {
    new URLSearchParams(window.location.search).forEach((value, key) => {
      result[key] = value;
    });
  }
  return result;
}

export function getEffectiveParam(key: string): string | null {
  return getEffectiveParams()[key] ?? null;
}
